/* eslint-disable react/prop-types */

import React, { useContext, useEffect } from "react";
import { PropTypes } from "prop-types";
import { graphql } from "gatsby";
import { clearAllBodyScrollLocks } from "body-scroll-lock";

import { AppContext } from "~context/AppContext";
import { DocumentContext } from "~context/DocumentContext";

import Layout from "~components/Layout";
import SEO from "~components/SEO";
import Banner from "~components/case-study/Banner";
import Services from "~components/case-study/Services";
import Gallery from "~components/case-study/Gallery";
import Image from "~components/case-study/Image";
import List from "~components/case-study/List";
import Text from "~components/case-study/Text";

const CaseStudyPage = ({ data, location }) => {
  const {
    setHeaderBgColor,
    setHeaderFgColor,
    mobileMenuOpen,
    setHeaderButtonColor,
    setNavigating
  } = useContext(AppContext);
  const { scrollTop, windowWidth } = useContext(DocumentContext);

  //

  const { frontmatter } = data.markdownRemark;

  //

  useEffect(() => {
    setNavigating(false);
    setHeaderBgColor(frontmatter.bannerBackground);
    setHeaderFgColor(frontmatter.bannerFontColor);
    setHeaderButtonColor({
      background: frontmatter.headerButtonColor,
      text: frontmatter.headerButtonTextColor
    });
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) {
      setHeaderBgColor(frontmatter.bannerBackground);
      setHeaderFgColor(frontmatter.bannerFontColor);
      setHeaderButtonColor({
        background: frontmatter.headerButtonColor,
        text: frontmatter.headerButtonTextColor
      });
    } else {
      setHeaderBgColor(`navy`);
      setHeaderFgColor(`off-white`);
      setHeaderButtonColor({ background: `off-white`, text: `navy` });
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (scrollTop > windowWidth * 0.5625) {
      setHeaderBgColor(frontmatter.backgroundColor);
      setHeaderFgColor(frontmatter.headerTextColor);
    } else {
      setHeaderBgColor(frontmatter.bannerBackground);
      setHeaderFgColor(frontmatter.bannerHeaderTextColor);
    }
  }, [scrollTop]);

  //

  const sectionJSX = [];

  if (frontmatter?.sections?.[0]) {
    frontmatter.sections.forEach((section, sectionIndex) => {
      const sectionKey = `section-${sectionIndex}`;

      switch (section.type) {
        case `gallery`:
          sectionJSX.push(
            <Gallery
              key={sectionKey}
              section={section}
              title={frontmatter.title}
              sectionKey={sectionKey}
            />
          );

          break;

        case `image`:
          sectionJSX.push(
            <Image
              key={sectionKey}
              section={section}
              title={frontmatter.title}
            />
          );

          break;

        case `list`:
          sectionJSX.push(
            <List
              key={sectionKey}
              section={section}
              sectionKey={sectionKey}
              bannerFont={frontmatter.bannerFont}
            />
          );

          break;

        case `text`:
          sectionJSX.push(
            <Text
              key={sectionKey}
              section={section}
              bannerFont={frontmatter.bannerFont}
            />
          );

          break;

        default:
          break;
      }
    });
  }

  //

  useEffect(() => {
    clearAllBodyScrollLocks();
  }, []);

  //

  return (
    <>
      <SEO
        customTitle={frontmatter.title}
        customDescription={frontmatter.seoDescription}
        customKeywords={frontmatter.seoKeywords}
        path={location.pathname}
      />

      <Layout
        className={`case-study-page w-full relative bg-${frontmatter.backgroundColor} text-${frontmatter.fontColor}`}
      >
        <Banner
          heading={frontmatter.heading}
          bannerBackground={frontmatter.bannerBackground}
          bannerFontColor={frontmatter.bannerFontColor}
          bannerFont={frontmatter.bannerFont}
        />

        <Services
          tagline={frontmatter.tagline}
          services={frontmatter.services}
          fontColor={frontmatter.fontColor}
        />

        {sectionJSX}
      </Layout>
    </>
  );
};

CaseStudyPage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      frontmatter: PropTypes.shape({})
    })
  }).isRequired,
  location: PropTypes.shape({}).isRequired
};

export default CaseStudyPage;

export const query = graphql`
  query CaseStudyPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      frontmatter {
        title
        backgroundColor
        bannerBackground
        fontColor
        headerTextColor
        headerButtonTextColor
        headerButtonColor
        bannerHeaderTextColor
        bannerFont
        bannerFontColor
        imageAspect
        heading
        tagline
        services {
          text
          type
          description
        }
        sections {
          type
          backgroundColor
          borderTop
          fontColor
          heading
          contentType
          contentLeftHeading
          contentLeft
          contentRightHeading
          contentRight
          image {
            childImageSharp {
              fluid(maxWidth: 1440, quality: 75) {
                ...GatsbyImageSharpFluid_withWebp_noBase64
              }
            }
            publicURL
          }
          rows {
            image {
              childImageSharp {
                fluid(maxWidth: 1440, quality: 75) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
              publicURL
            }
            imageTop {
              childImageSharp {
                fluid(maxWidth: 1024, quality: 75) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
              publicURL
            }
            imageBottom {
              childImageSharp {
                fluid(maxWidth: 1024, quality: 75) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
              publicURL
            }
            imageLeft {
              childImageSharp {
                fluid(maxWidth: 1024, quality: 75) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
              publicURL
            }
            imageRight {
              childImageSharp {
                fluid(maxWidth: 1024, quality: 75) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
              publicURL
            }
            imageRightTop {
              childImageSharp {
                fluid(maxWidth: 1024, quality: 75) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
              publicURL
            }
            imageRightBottom {
              childImageSharp {
                fluid(maxWidth: 1024, quality: 75) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
              publicURL
            }
            type
          }
          items {
            heading
            subheading
            content
          }
        }
        seoDescription
        seoKeywords
      }
    }
  }
`;
